/**
 * Global /approvals page — cross-evaluation queue of "my pending decisions."
 * Lists every approval row where the logged-in user is the active approver
 * (lowest-sequence PENDING on an UNDER_REVIEW evaluation).
 *
 * Two display modes:
 *   - List view (default): linear card list with eval name, sequence, sites,
 *     decide button. Each row is a Link → /evaluations/:id/approvals.
 *   - Kanban view: groups rows into buckets:
 *       "Just submitted"  — no prior approvers have approved yet
 *       "In your queue"   — at least one prior approver has approved
 *
 * The third SSP bucket "Sent back for changes" requires audit-log history
 * (Phase J) to detect reliably; the bucket is rendered as a placeholder
 * with a deferred-state hint until then.
 *
 * Linear: MYD-205 (D6).
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
	UserCheck,
	ChevronRight,
	Loader2,
	List,
	LayoutGrid,
	Building2,
	ArrowRight,
	Forward,
	RefreshCw,
} from 'lucide-react'

import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import RightPanel from 'components/RightPanel/RightPanel'
import SidebarPartners from 'components/Sidebars/SidebarPartners'
import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { cn } from 'lib/utils'
import { fetchMyPendingApprovals } from 'slices/approvalSlice'

type ViewMode = 'list' | 'kanban'

interface PendingRow {
	_id: string
	sequence: number
	priorApprovedCount: number
	siteCount: number
	delegatedFrom?: { name?: string; email?: string } | null
	evaluation: {
		_id: string
		name: string
		status: string
		marketArea?: string
		updatedAt: string
	}
}

function evalUrl(evalId: string): string {
	return `/evaluations/${evalId}/approvals`
}

function formatDate(iso: string): string {
	try {
		return new Date(iso).toLocaleDateString('en-US')
	} catch {
		return iso
	}
}

const PendingRowCard = ({ row }: { row: PendingRow }) => {
	const isJustSubmitted = row.priorApprovedCount === 0
	return (
		<Link
			to={evalUrl(row.evaluation._id)}
			className={cn(
				'flex items-center gap-3 px-4 py-3 rounded-md border bg-white no-underline transition-colors',
				'hover:border-[#0080fe] hover:shadow-sm group',
			)}
		>
			<div className="w-10 h-10 rounded-full bg-[#0080fe]/10 text-[#0080fe] flex items-center justify-center shrink-0 font-semibold">
				#{row.sequence}
			</div>
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="font-semibold text-sm text-gray-900 truncate">
						{row.evaluation.name}
					</span>
					{row.evaluation.marketArea && (
						<Badge className="text-[10px] bg-gray-100 text-gray-700">
							{row.evaluation.marketArea}
						</Badge>
					)}
					{isJustSubmitted ? (
						<Badge className="text-[10px] bg-emerald-100 text-emerald-700">
							Just submitted
						</Badge>
					) : (
						<Badge className="text-[10px] bg-blue-100 text-blue-700">
							{row.priorApprovedCount} prior approval
							{row.priorApprovedCount === 1 ? '' : 's'}
						</Badge>
					)}
					{row.delegatedFrom && (
						<Badge className="text-[10px] bg-blue-100 text-blue-700 gap-1">
							<Forward className="w-3 h-3" />
							Delegated
						</Badge>
					)}
				</div>
				<div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
					<span className="inline-flex items-center gap-1">
						<Building2 className="w-3 h-3" />
						{row.siteCount} site{row.siteCount === 1 ? '' : 's'}
					</span>
					<span>·</span>
					<span>Updated {formatDate(row.evaluation.updatedAt)}</span>
					{row.delegatedFrom?.name && (
						<>
							<span>·</span>
							<span>From {row.delegatedFrom.name}</span>
						</>
					)}
				</div>
			</div>
			<div className="text-xs font-medium text-[#0080fe] flex items-center gap-1 shrink-0 group-hover:gap-1.5 transition-all">
				Decide
				<ArrowRight className="w-3.5 h-3.5" />
			</div>
		</Link>
	)
}

const KanbanColumn = ({
	title,
	hint,
	rows,
}: {
	title: string
	hint?: string
	rows: PendingRow[]
}) => (
	<div className="flex-1 min-w-0">
		<div className="px-3 py-2 mb-2 rounded-md bg-gray-100">
			<div className="text-xs font-semibold uppercase tracking-wider text-gray-700">
				{title} ({rows.length})
			</div>
			{hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
		</div>
		{rows.length === 0 ? (
			<div className="px-3 py-6 text-center text-xs text-gray-400 italic border border-dashed border-gray-200 rounded-md">
				Nothing here.
			</div>
		) : (
			<div className="space-y-2">
				{rows.map((row) => (
					<PendingRowCard key={row._id} row={row} />
				))}
			</div>
		)}
	</div>
)

const ApprovalsPage = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [view, setView] = useState<ViewMode>('list')

	const rows: PendingRow[] = useSelector(
		(state: any) => state.approvals?.myPending || [],
	)
	const loaded: boolean = useSelector(
		(state: any) => Boolean(state.approvals?.myPendingLoaded),
	)

	useEffect(() => {
		// @ts-ignore — thunk
		dispatch(fetchMyPendingApprovals())
	}, [dispatch])

	const buckets = useMemo(() => {
		const justSubmitted: PendingRow[] = []
		const inQueue: PendingRow[] = []
		for (const row of rows) {
			if (row.priorApprovedCount === 0) justSubmitted.push(row)
			else inQueue.push(row)
		}
		return { justSubmitted, inQueue }
	}, [rows])

	const isLoading = !loaded
	const isEmpty = loaded && rows.length === 0

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN
				mobileOpen={mobileMenuOpen}
				onMobileClose={() => setMobileMenuOpen(false)}
			/>
			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
				<main className="flex-1 overflow-y-auto p-6">
					<div className="max-w-5xl mx-auto space-y-5">
						{/* Header */}
						<div className="flex items-start justify-between gap-3 flex-wrap">
							<div>
								<h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
									<UserCheck className="w-5 h-5 text-[#0080fe]" />
									My pending approvals
								</h1>
								<p className="text-sm text-gray-500 mt-0.5">
									Cross-evaluation queue of decisions waiting on you. Click any row to
									open that evaluation's approval timeline.
								</p>
							</div>
							<div className="flex items-center gap-2">
								<button
									type="button"
									onClick={() => {
										// @ts-ignore — thunk
										dispatch(fetchMyPendingApprovals())
									}}
									title="Refresh"
									aria-label="Refresh"
									className="p-2 rounded-md border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50"
								>
									<RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
								</button>
								<div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
									<button
										type="button"
										onClick={() => setView('list')}
										className={cn(
											'px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition-colors',
											view === 'list'
												? 'bg-[#0080fe] text-white'
												: 'text-gray-600 hover:bg-gray-100',
										)}
									>
										<List className="w-3.5 h-3.5" />
										List
									</button>
									<button
										type="button"
										onClick={() => setView('kanban')}
										className={cn(
											'px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition-colors',
											view === 'kanban'
												? 'bg-[#0080fe] text-white'
												: 'text-gray-600 hover:bg-gray-100',
										)}
									>
										<LayoutGrid className="w-3.5 h-3.5" />
										Kanban
									</button>
								</div>
							</div>
						</div>

						{/* Body */}
						{isLoading ? (
							<Card>
								<CardContent className="py-10">
									<div className="flex items-center justify-center gap-2 text-sm text-gray-500">
										<Loader2 className="w-4 h-4 animate-spin" />
										Loading pending decisions…
									</div>
								</CardContent>
							</Card>
						) : isEmpty ? (
							<Card>
								<CardContent className="py-12">
									<div className="text-center">
										<UserCheck className="mx-auto h-10 w-10 text-gray-300 mb-2" />
										<p className="text-base font-medium text-gray-700">
											Nothing waiting on you. Nice.
										</p>
										<p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
											When evaluations advance to a sequence where you're the next
											approver, they'll appear here.
										</p>
									</div>
								</CardContent>
							</Card>
						) : view === 'list' ? (
							<Card>
								<CardHeader className="pb-3">
									<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
										<UserCheck className="w-4 h-4" />
										Awaiting your decision ({rows.length})
									</h4>
								</CardHeader>
								<CardContent>
									<div className="space-y-2">
										{rows.map((row) => (
											<PendingRowCard key={row._id} row={row} />
										))}
									</div>
								</CardContent>
							</Card>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
								<KanbanColumn
									title="Just submitted"
									hint="No prior approvers yet."
									rows={buckets.justSubmitted}
								/>
								<KanbanColumn
									title="In your queue"
									hint="Prior approvers have signed off."
									rows={buckets.inQueue}
								/>
								<KanbanColumn
									title="Sent back for changes"
									hint="Surfaces here once Phase J audit data lands."
									rows={[]}
								/>
							</div>
						)}

						{/* Subtle footer link to keep the chevron-right affordance
						    consistent with the row hover pattern. */}
						{!isEmpty && !isLoading && (
							<div className="flex items-center justify-end text-xs text-gray-400">
								<ChevronRight className="w-3 h-3 mr-1" />
								Tap any row to open its full approval timeline.
							</div>
						)}
					</div>
				</main>

				{/* Partners, so an approver's profile and contact details are a
				    click away from the approvals that name them (MYD-533). */}
				<RightPanel storageKey="approvals" label="Partnerships panel">
					<SidebarPartners />
				</RightPanel>
				</div>
			</div>
		</div>
	)
}

export default ApprovalsPage
